import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetActionsStatsQuery,
  GetActionsStatsData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetActionsStatsState
  extends MergeRequestState<GetActionsStatsData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getActionsStats,
  isFetching: (state: ReduxState) => state.server.getActionsStats.isFetching,
  data: (state: ReduxState) => state.server.getActionsStats.data,
  error: (state: ReduxState) => state.server.getActionsStats.error,
};

const initialState: GetActionsStatsState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getActionsStats';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      query,
      config,
    }: { query: GetActionsStatsQuery; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getActionsStats(query, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetActionsStats {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getActionsStats: ServerGetActionsStats = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
